import type { ProposalResult } from '../api/client';

export interface ConfirmableVoiceProposal {
  proposalId: string;
  target: string;
  text: string;
  crossFocus: boolean;
}

export interface VoiceProposalGate {
  registerFocus: (voiceSessionId: string, epoch: number, target: string) => Promise<boolean>;
  submitProposal: (proposal: {
    voiceSessionId: string;
    focusEpoch: number;
    targetSession: string;
    proposedText: string;
    targetContext: string;
  }) => Promise<ProposalResult>;
  confirmProposal: (proposalId: string) => Promise<ProposalResult>;
}

export interface VoiceProposalConfirmation {
  confirmed: boolean;
  reason?: string;
}

function failureReason(result: ProposalResult, fallback: string): string {
  if (result.reason) return result.reason;
  if (result.error) return result.error;
  if (typeof result.detail === 'string') return result.detail;
  if (Array.isArray(result.detail)) {
    const messages = result.detail.map((entry) => entry.msg).filter(Boolean);
    if (messages.length > 0) return messages.join('; ');
  }
  return fallback;
}

export function confirmedVoiceRelay(result: ProposalResult): VoiceProposalConfirmation {
  if (result.status >= 200 && result.status < 300 && result.relay_sent === true) {
    return { confirmed: true };
  }
  const fallback = result.status >= 200 && result.status < 300
    ? 'relay_not_sent'
    : `HTTP ${result.status || 'network'}`;
  return { confirmed: false, reason: failureReason(result, fallback) };
}

/**
 * Executes an operator-approved talkback proposal and only reports success when
 * cloud-api confirms that the relay was actually sent. Cross-focus proposals
 * are submitted after the operator's approval; already parked proposals go
 * straight through the confirmation endpoint.
 */
export async function confirmVoiceProposal(
  proposal: ConfirmableVoiceProposal,
  editedText: string | undefined,
  voiceSessionId: string,
  focusEpoch: number,
  gate: VoiceProposalGate,
): Promise<VoiceProposalConfirmation> {
  if (!proposal.crossFocus) {
    if (!proposal.proposalId) return { confirmed: false, reason: 'proposal_missing' };
    return confirmedVoiceRelay(await gate.confirmProposal(proposal.proposalId));
  }

  const focusRegistered = await gate.registerFocus(voiceSessionId, focusEpoch, proposal.target);
  if (!focusRegistered) return { confirmed: false, reason: 'focus_registration_failed' };

  const submitted = await gate.submitProposal({
    voiceSessionId,
    focusEpoch,
    targetSession: proposal.target,
    proposedText: editedText || proposal.text,
    targetContext: 'agent',
  });
  if (submitted.gate === 'auto_ok') return confirmedVoiceRelay(submitted);
  if (submitted.gate === 'denied') {
    return { confirmed: false, reason: failureReason(submitted, 'denied') };
  }
  if (submitted.gate !== 'confirm_required' || !submitted.proposal_id) {
    return { confirmed: false, reason: failureReason(submitted, 'invalid_gate_response') };
  }
  return confirmedVoiceRelay(await gate.confirmProposal(submitted.proposal_id));
}

/**
 * Erkennungs-Artefakte, die Whisper in Stille hineinhört.
 *
 * Der Fund gehört AiApi (12.08.): Ihr Arcturian-Prompt verbietet sie als
 * REGEL 4 — *„was du nicht verstanden hast, führst du nicht aus"* —, im
 * Talkback-Prompt fehlte die Regel. Cloud hat daraufhin sein Tor geprüft:
 * `classify()` ist ein reiner Gefahren-Ausdruck, und „Vielen Dank fürs
 * Zuschauen" enthält kein Gefahrenwort. Also `safe`.
 *
 * UND HIER SCHLIESST SICH DER KREIS, DEN ICH SELBST NACHGESEHEN HABE:
 * Bei `gate === 'auto_ok'` sendet mein Client **sofort und ohne Chip**. Der
 * Bestätigungs-Chip erscheint nur, wenn der Server ihn verlangt oder das Ziel
 * außerhalb des Fokus liegt. AiApis erste Entwarnung — „es geht nichts still
 * hinaus, ihr habt ja den Chip" — war damit falsch, und ihre Rücknahme richtig:
 * Ein Artefakt, das an den aktuellen Fokus adressiert ist, geht still hinaus.
 *
 * Deshalb hier, VOR dem Tor: Besteht die ganze Nachricht nur aus einem
 * bekannten Artefakt, ist sie keine Nachricht. Bewusst nur bei vollständiger
 * Übereinstimmung — wer den Satz zitiert („schreib ihm, dass da ‚Vielen Dank
 * fürs Zuschauen' stand"), meint ihn und soll ihn senden dürfen.
 */
const RECOGNITION_ARTIFACTS: readonly RegExp[] = [
  /^untertitelung\s+(aufgrund\s+der\s+audioqualit(ae|ä)t\s+nicht\s+m(oe|ö)glich|des\s+zdf)/i,
  /^untertitel\s+(von|im\s+auftrag)\b/i,
  /^vielen\s+dank\s+f(ue|ü)rs?\s+zuschauen/i,
  /^(thanks?\s+for\s+watching|subtitles?\s+by)\b/i,
  /^amara\.org/i,
];

/** Ist die ganze Aeusserung nur ein Erkennungs-Artefakt? */
export function isRecognitionArtifact(text: string): boolean {
  const cleaned = (text || '').trim().replace(/[.!…]+$/, '').trim();
  if (!cleaned) return false;
  // Laenge als zweite Bedingung: Ein Artefakt ist ein kurzer Standardsatz.
  // Wer laenger schreibt und ihn nur zitiert, faellt hier nicht hinein.
  if (cleaned.length > 120) return false;
  return RECOGNITION_ARTIFACTS.some((pattern) => pattern.test(cleaned));
}
